import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import addAdjustmentImage from "assets/overrideGuide/add-adjustment.webp";
import addRemoveButtonsImage from "assets/overrideGuide/add-remove-buttons.webp";
import cancelOverrideImage from "assets/overrideGuide/cancel-override.webp";
import chargeListImage from "assets/overrideGuide/charge-list.webp";
import continueButtonImage from "assets/overrideGuide/continue-button.webp";
import editRecipeButtonImage from "assets/overrideGuide/edit-recipe-button.webp";
import heatQueueImage from "assets/overrideGuide/heat-queue.webp";
import invalidAdjustmentImage from "assets/overrideGuide/invalid-adjustment.webp";
import overriddenHeatsImage from "assets/overrideGuide/overridden-heats.webp";
import overrideDetailsImage from "assets/overrideGuide/override-details.webp";
import reasonImage from "assets/overrideGuide/reason.webp";
import saveButtonImage from "assets/overrideGuide/save-button.webp";
import selectHeatsImage from "assets/overrideGuide/select-heats.webp";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { ReactNode } from "react";
import { Link } from "react-router";

interface GuideStep {
  instruction: ReactNode;
  screenshot: { source: string; altText: string };
  note?: { severity: "info" | "warning"; text: string };
}

interface GuideSection {
  title: string;
  steps: GuideStep[];
}

const HEAT_QUEUE_LINK = { to: "..", relative: "path" } as const;

const OVERRIDE_GUIDE_TITLE = "How to create an override";

const GUIDE_INTRO =
  "How to create an override that adjusts the materials in upcoming heats, and how to view or cancel it.";

const KEY_TERMS_TITLE = "Key terms";

const SELECTION_RULE = "Heats must be consecutive, and have the same recipe.";

const KEY_TERMS =
  "An override changes the recipe for one or more upcoming heats. Each override is made up of one or more adjustments: an amount of one material added to or removed from one charge.";

export const OverrideGuidePage = () => {
  const { t } = useTenantTranslation();
  const { heat_override_max_heats: maxHeats } = useTenantData();

  const sections: GuideSection[] = [
    {
      title: "Opening the Heat Queue",
      steps: [
        {
          instruction: (
            <>
              {"To view the Heat Queue, click "}
              <MuiLink component={Link} {...HEAT_QUEUE_LINK}>
                {"here"}
              </MuiLink>
              {". It should look like this:"}
            </>
          ),
          screenshot: { source: heatQueueImage, altText: "The Heat Queue" },
        },
      ],
    },
    {
      title: "Creating an override",
      steps: [
        {
          instruction:
            "Select the heats you wish to override by checking the boxes on the left of the table:",
          screenshot: { source: selectHeatsImage, altText: "Selecting heats" },
          note: {
            severity: "info",
            text:
              maxHeats === null
                ? SELECTION_RULE
                : `${SELECTION_RULE} A maximum of ${maxHeats} heats can be overridden at once.`,
          },
        },
        {
          instruction:
            "Once you have selected the heats, click the “Edit recipe” button:",
          screenshot: {
            source: editRecipeButtonImage,
            altText: "The Edit recipe button",
          },
        },
        {
          instruction:
            "You will see a list of all charges in the heats’ recipe:",
          screenshot: {
            source: chargeListImage,
            altText: "The charges in the selected heats’ recipe",
          },
        },
        {
          instruction:
            "Click “Add adjustment for charge X” next to the charge you want to adjust. For each adjustment, choose the material and the amount you want to change it by:",
          screenshot: {
            source: addAdjustmentImage,
            altText: "Choosing a material and amount",
          },
        },
        {
          instruction:
            "Use the “+/-” buttons to denote whether you are adding material or removing it:",
          screenshot: {
            source: addRemoveButtonsImage,
            altText: "The add (+) and remove (−) buttons",
          },
          note: {
            severity: "warning",
            text: "You cannot remove more of a material from the charge than the charge has in the first place. For example, if the charge contains 10,000 lbs of Shredded, if you try to remove more than 10,000 lbs of Shredded you will get an error.",
          },
        },
        {
          instruction: "When you’ve added all adjustments, click “Continue”:",
          screenshot: {
            source: continueButtonImage,
            altText: "The Continue button",
          },
        },
        {
          instruction:
            "If your adjustments are invalid — for example if you’ve tried to remove more of a material from a charge than the charge contains — “Continue” will be grey and you will see a message explaining what the error is. Fix the errors before continuing.",
          screenshot: {
            source: invalidAdjustmentImage,
            altText: "An invalid adjustment and its error message",
          },
        },
        {
          instruction:
            "On the next page, select the reason and subreason for the override. You can add an optional comment:",
          screenshot: {
            source: reasonImage,
            altText: "Choosing a reason and subreason",
          },
        },
        {
          instruction: "Click “Save” when ready:",
          screenshot: { source: saveButtonImage, altText: "The Save button" },
        },
        {
          instruction: "The override will now be visible on the Heat Queue:",
          screenshot: {
            source: overriddenHeatsImage,
            altText: "Overridden heats in the Heat Queue",
          },
        },
      ],
    },
    {
      title: "Viewing or cancelling an override",
      steps: [
        {
          instruction:
            "Click “View Details” to see the details of the override:",
          screenshot: {
            source: overrideDetailsImage,
            altText: "Override details",
          },
        },
        {
          instruction:
            "To cancel the override — for example if you made a mistake — click “Cancel”:",
          screenshot: {
            source: cancelOverrideImage,
            altText: "The Cancel link",
          },
        },
      ],
    },
  ];

  return (
    <AccountPageLayout
      title={OVERRIDE_GUIDE_TITLE}
      actionButton={
        <Button
          component={Link}
          {...HEAT_QUEUE_LINK}
          startIcon={<ArrowBackRoundedIcon />}
        >
          {t("heatQueue")}
        </Button>
      }
    >
      <Stack sx={{ gap: 6, maxWidth: 960, pb: 6 }}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {GUIDE_INTRO}
        </Typography>
        <Alert severity="info">
          <AlertTitle>{KEY_TERMS_TITLE}</AlertTitle>
          {KEY_TERMS}
        </Alert>
        {sections.map((section, sectionIndex) => (
          <Stack key={section.title} component="section" sx={{ gap: 4 }}>
            <Typography variant="h2">
              {`${sectionIndex + 1}. ${section.title}`}
            </Typography>
            {section.steps.map((step, stepIndex) => (
              <GuideStepView
                key={step.screenshot.altText}
                stepNumber={stepIndex + 1}
                step={step}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </AccountPageLayout>
  );
};

const GuideStepView = ({
  stepNumber,
  step,
}: {
  stepNumber: number;
  step: GuideStep;
}) => (
  <Stack direction="row" sx={{ gap: 2, alignItems: "flex-start" }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "fontWeightBold" }}>
        {stepNumber}
      </Typography>
    </Box>
    <Stack sx={{ gap: 2, minWidth: 0, flexGrow: 1 }}>
      <Typography variant="body1">{step.instruction}</Typography>
      {step.note && (
        <Alert severity={step.note.severity}>{step.note.text}</Alert>
      )}
      <Box
        component="img"
        src={step.screenshot.source}
        alt={step.screenshot.altText}
        loading="lazy"
        sx={{
          display: "block",
          alignSelf: "flex-start",
          maxWidth: "100%",
          maxHeight: 640,
          border: 1,
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      />
    </Stack>
  </Stack>
);
